import {
  CookieKey,
  DEFAULT_EXPIRE_DAYS,
  EMAIL_EXIST_OTHER_AUTH_METHOD_ISSUES,
  EmailLoginError,
  EmailStatusCode,
  ErrorMessages,
  GUEST_REFRESH_TOKEN_EXPIRE_DAYS,
  LocalStorageKey,
  MessageField,
  REFRESH_TOKEN_EXPIRE_DAYS,
  SignUpSource,
} from '@/constants';
import { sendVerificationEmail } from '@/fetchers';
import { updateTrackedShipments } from '@/fetchers/umsAPI/tracking.fetchers';
import {
  type AuthenticateEMSPayload,
  type AuthenticateEmailPayload,
  type AuthenticatePMPayload,
  authenticateEMSFetcher,
  authenticateEmailFetcher,
  authenticatePMFetcher,
  guestLoginFetcher,
  logoutAll as logoutAllFetcher,
  logout as logoutFetcher,
} from '@/fetchers/umsAPI/umsAuthenticator.fetchers';
import type { IAuthenticateSuccessfulResponse, LanguageCode } from '@/interfaces';
import type { PmtOnboardingPopupCta } from '@/interfaces/tracking';
import { userInfoModify } from '@/utils';
import { clearSignedInBrowserState } from '@/utils/auth';
import type { OriginalUserInfo } from '@/utils/umsDataModify.utils';
import { useStatsigUser } from '@statsig/react-bindings';
import Cookies from 'js-cookie';
import { useCallback } from 'react';
import useGaEventBaseParams from './useGaEventBaseParams.hook';
import useGeo from './useGeo.hooks';
import useGlobalState from './useGlobalState.hooks';

/**
 * Hook that provides authentication related functions
 */

export const useAuth = () => {
  const { isLoggedIn, setIsLoggedIn, setUserInfo, setFirestoreUuid, pmtCurrentLanguageCode, clearUserInfo } =
    useGlobalState();
  const { geoInfo } = useGeo();
  const getGaEventBaseParams = useGaEventBaseParams();
  const { updateUserSync } = useStatsigUser();

  /**
   * Merges tracked shipments from guest accounts into the logged in user's account
   * - Gets guest UUIDs from cookies and localStorage
   * - Updates tracked shipments in UMS with the guest UUIDs
   * - Removes temporary TIO UUID from localStorage on success
   */
  const mergeShipments = async () => {
    const uuids: string[] = [];
    const guestUuid = Cookies.get(CookieKey.UserId);
    const tioGuestUuid = localStorage.getItem(LocalStorageKey.tioGuestUuid);

    if (guestUuid) uuids.push(guestUuid);
    if (tioGuestUuid) uuids.push(tioGuestUuid);

    if (!uuids.length) return;

    const responseStatus = await updateTrackedShipments({
      language: pmtCurrentLanguageCode,
      uuids,
    });

    if (responseStatus === 200) {
      localStorage.removeItem(LocalStorageKey.tioGuestUuid);
    }
  };

  /**
   * This function is used to set necessary cookies and global state to treat user as logged in
   * - Sets access token and refresh token in cookies
   * - Sets user info in global state
   * - Sets firestore uuid in global state
   * - Merge current tracked shipments as a guest to the user's account
   */
  const PMLogin = (userInfo: OriginalUserInfo) => {
    if (!userInfo) return;

    setIsLoggedIn(true);

    const modifiedUserInfo = userInfoModify(userInfo);
    setUserInfo(modifiedUserInfo);
    setFirestoreUuid(modifiedUserInfo.id);
    // Update Statsig user info
    updateUserSync({
      userID: modifiedUserInfo.id,
      custom: {
        email_sync_opt_in: String(modifiedUserInfo.emailSyncOptIn),
        newsletter_opt_in: String(modifiedUserInfo.newsletterOptIn),
        subscribe_to_email_summary: String(modifiedUserInfo.subscribeToEmailSummary),
        opt_in_date: modifiedUserInfo.optInDate ?? undefined,
        sign_up_source: modifiedUserInfo.signUpSource ?? undefined,
        user_source: modifiedUserInfo.userSource ?? undefined,
        last_time_enable_ems: modifiedUserInfo.lastTimeEnableEms ?? undefined,
        last_time_disable_ems: modifiedUserInfo.lastTimeDisableEms ?? undefined,
        email_sync_reauthentication_count: modifiedUserInfo.emailSyncReauthenticationCount,
        push_notification_opt_in: modifiedUserInfo.pushNotificationOptIn,
      },
    });
    window.gtag?.('set', { user_id: modifiedUserInfo.id });
  };

  // [Login by Google/LinkedIn] Authenticate with email sync
  const authEMS = async ({
    code,
    signUpSource = SignUpSource.ConsumerWeb,
    newsletterOptIn = false,
    scope,
    country,
    userId,
    redirectUri,
    gaClientId,
    gaSessionId,
    eventTimestamp,
    cta,
    authMethod,
    utmParams,
  }: AuthenticateEMSPayload) => {
    try {
      const response = await authenticateEMSFetcher({
        code,
        scope,
        signUpSource,
        newsletterOptIn,
        country,
        userId,
        redirectUri,
        gaClientId,
        gaSessionId,
        eventTimestamp,
        cta,
        authMethod,
        utmParams,
      });

      if (!response.access_token || !response.user_info) {
        throw new Error(ErrorMessages[MessageField.EMAIL_SYNC_CANNOT_AUTHORIZED].error);
      }

      if (!isLoggedIn) {
        // set access token + refresh token --> merge shipment --> set user id
        Cookies.set(CookieKey.AccessToken, response.access_token, { expires: DEFAULT_EXPIRE_DAYS });
        Cookies.set(CookieKey.RefreshToken, response.refresh_token, { expires: REFRESH_TOKEN_EXPIRE_DAYS });

        // This is to merge current tracked shipments as a guest to the user's account
        // Shipments should be merged before setting user id in cookie
        mergeShipments();

        Cookies.set(CookieKey.UserId, response.user_info.id, { expires: DEFAULT_EXPIRE_DAYS });

        PMLogin(response.user_info);
      }

      return response;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  // [Login by Google/LinkedIn] Authenticate without email sync
  const authPM = async ({
    mediumOrigin,
    code,
    signUpSource = SignUpSource.ConsumerWeb,
    newsletterOptIn = false,
    country,
    gaClientId,
    gaSessionId,
    eventTimestamp,
    cta,
    authMethod,
  }: AuthenticatePMPayload) => {
    try {
      const response = await authenticatePMFetcher({
        mediumOrigin,
        code,
        country,
        signUpSource,
        newsletterOptIn,
        gaClientId,
        gaSessionId,
        eventTimestamp,
        cta,
        authMethod,
      });

      const hasError = 'error' in response;

      if (hasError) {
        throw new Error(ErrorMessages[MessageField.AUTHENTICATE_ERROR].error);
      }

      // set access token + refresh token --> merge shipment --> set user id
      Cookies.set(CookieKey.AccessToken, response.access_token, { expires: DEFAULT_EXPIRE_DAYS });
      Cookies.set(CookieKey.RefreshToken, response.refresh_token, { expires: REFRESH_TOKEN_EXPIRE_DAYS });

      // This is to merge current tracked shipments as a guest to the user's account
      // Shipments should be merged before setting user id in cookie
      mergeShipments();

      Cookies.set(CookieKey.UserId, response.user_info.id, { expires: DEFAULT_EXPIRE_DAYS });

      PMLogin(response.user_info);

      return response;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  // [Login by Email] Authenticate with email
  const authEmail = async ({
    email,
    browserUuid,
    signUpSource = SignUpSource.ConsumerWeb,
    newsletterOptIn = false,
    country,
    gaClientId,
    gaSessionId,
    eventTimestamp,
    cta,
    authMethod,
    language,
    variant,
    popupVariant,
  }: AuthenticateEmailPayload & {
    language?: LanguageCode;
    variant?: string;
    popupVariant?: string;
  }) => {
    try {
      const response = await authenticateEmailFetcher({
        email,
        browserUuid,
        country,
        signUpSource,
        newsletterOptIn,
        gaClientId,
        gaSessionId,
        eventTimestamp,
        cta,
        authMethod,
      });

      const hasError = 'error' in response;
      if (hasError) return;

      const authResponse = response as IAuthenticateSuccessfulResponse;

      switch (authResponse.status) {
        case EmailStatusCode.Verified:
          Cookies.set(CookieKey.AccessToken, authResponse.access_token, { expires: DEFAULT_EXPIRE_DAYS });
          Cookies.set(CookieKey.RefreshToken, authResponse.refresh_token, { expires: REFRESH_TOKEN_EXPIRE_DAYS });
          Cookies.set(CookieKey.UserId, authResponse.user_info.id, { expires: DEFAULT_EXPIRE_DAYS });

          PMLogin(authResponse.user_info);
          break;

        case EmailStatusCode.Unverified: {
          // set access token + refresh token --> merge shipment --> set user id
          Cookies.set(CookieKey.AccessToken, authResponse.access_token, { expires: DEFAULT_EXPIRE_DAYS });
          Cookies.set(CookieKey.RefreshToken, authResponse.refresh_token, { expires: REFRESH_TOKEN_EXPIRE_DAYS });

          // Shipments should be merged before setting user id in cookie
          mergeShipments();

          Cookies.set(CookieKey.UserId, authResponse.user_info.id, { expires: DEFAULT_EXPIRE_DAYS });
          Cookies.set(CookieKey.BrowserUuid, authResponse.browser_uuid, { expires: DEFAULT_EXPIRE_DAYS });

          setUserInfo(userInfoModify(authResponse.user_info));
          setFirestoreUuid(authResponse.user_info.id);

          if (language) {
            await sendVerificationEmail({
              email,
              browserUuid: authResponse.browser_uuid,
              language,
              source: signUpSource,
              cta: cta as PmtOnboardingPopupCta,
              variant,
              popupVariant,
            });
          }
          break;
        }

        case EmailStatusCode.ExistOtherIssues:
          throw {
            type: EMAIL_EXIST_OTHER_AUTH_METHOD_ISSUES,
            authMethod: EmailLoginError[authResponse.error_code],
          };
      }

      return authResponse;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  /**
   * What both kinds of logout do once the server has ended the session
   * - Clears all signed-in browser state
   * - Updates global state and reloads, since Firestore cannot be reused once terminated
   */
  const endBrowserSession = useCallback(async () => {
    window.gtag?.('set', { user_id: null });
    clearUserInfo();
    // Update Statsig user info
    updateUserSync({ userID: undefined });
    await clearSignedInBrowserState();
    window.location.reload();
  }, [clearUserInfo, updateUserSync]);

  /**
   * Handles user logout
   * - Tells the server to revoke the refresh token (best effort)
   * - Ends the browser session, whatever the server answered
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: logout is intentionally stable — deps would cause infinite re-renders
  const logout = useCallback(async () => {
    try {
      const refreshToken = Cookies.get(CookieKey.RefreshToken);
      if (refreshToken) {
        const gaEventBaseParams = getGaEventBaseParams();
        const res = await logoutFetcher({
          refreshToken,
          ...gaEventBaseParams,
        });
        if (res?.status_code !== 200) console.error('Logout error: unexpected status', res?.status_code);
      }
    } catch (error) {
      console.error('Logout error:', error);
    }

    await endBrowserSession();
  }, [endBrowserSession]);

  /**
   * Handles log out of all devices
   * - Tells the server to end every session of the user
   * - Only when the server confirms, ends the browser session like logout; otherwise nothing is cleared
   *
   * @returns false when the server did not confirm, so the caller can show an error and let the user retry
   */
  const logoutAll = useCallback(async () => {
    if (!(await logoutAllFetcher())) return false;

    await endBrowserSession();
    return true;
  }, [endBrowserSession]);

  const authGuest = async () => {
    const data = await guestLoginFetcher({ country: geoInfo.country });

    Cookies.set(CookieKey.AccessToken, data.access_token, { expires: DEFAULT_EXPIRE_DAYS });
    Cookies.set(CookieKey.RefreshToken, data.refresh_token, { expires: GUEST_REFRESH_TOKEN_EXPIRE_DAYS });
    Cookies.set(CookieKey.UserId, data.user_info.id, { expires: DEFAULT_EXPIRE_DAYS });

    setFirestoreUuid(data.user_info.id);

    return data;
  };

  return {
    authEMS,
    authPM,
    authEmail,
    logout,
    logoutAll,
    authGuest,
    PMLogin,
  };
};

export default useAuth;
